import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState(null); // {action: bool} or null

  const fetchPermissions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/permissions/me`, { withCredentials: true });
      setPermissions(data?.permissions || {});
    } catch {
      setPermissions({});
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
      await fetchPermissions();
    } catch {
      setUser(false);
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  }, [fetchPermissions]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const loginRes = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    let sessionUser = loginRes.data;
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      sessionUser = data;
    } catch {
      // Keep login response if /me is temporarily unavailable (e.g. backend restarting).
    }
    setUser(sessionUser);
    fetchPermissions().catch(() => setPermissions({}));
    return sessionUser;
  };

  const register = async (email, password, name, organizationName) => {
    const registerRes = await axios.post(
      `${API}/auth/register`,
      { email, password, name, organization_name: organizationName },
      { withCredentials: true },
    );
    let sessionUser = registerRes.data;
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      sessionUser = data;
    } catch {
      // Fall back to register payload when /me cannot be reached yet.
    }
    setUser(sessionUser);
    fetchPermissions().catch(() => setPermissions({}));
    return sessionUser;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(false);
    setPermissions(null);
  };

  const hasRole = (...roles) => user && roles.includes(user.role);
  const isAdmin = () => hasRole('super_admin', 'admin');
  const isSuperAdmin = () => hasRole('super_admin');
  const isImpersonating = () => Boolean(user?.impersonated_by_id);
  const stopImpersonate = async () => {
    await axios.post(`${API}/admin/stop-impersonate`, {}, { withCredentials: true });
    await checkAuth();
  };
  const isTrial = () => user && user.org_status === 'trial';
  const trialDaysLeft = () => {
    if (!user?.trial_ends_at) return 0;
    const diff = new Date(user.trial_ends_at) - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  /**
   * Permission helper. Returns true if the current user can perform `action`.
   * - super_admin -> always true (defensive: even before perms load).
   * - while permissions are still loading, falls back to role-based defaults
   *   to avoid flickering UI ("everything denied for a frame").
   */
  const can = (action) => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    if (permissions && Object.prototype.hasOwnProperty.call(permissions, action)) {
      return Boolean(permissions[action]);
    }
    // Reasonable fallback before /permissions/me resolves: admin sees most things.
    return user.role === 'admin';
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, logout, checkAuth, hasRole,
      isAdmin, isSuperAdmin, isImpersonating, stopImpersonate, isTrial, trialDaysLeft,
      permissions, fetchPermissions, can,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
